import { keyframes } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import { Box, Flex, IconButton, Input, PaperAirplaneOutline, Text } from '@bonusly/design-system';

import { Bizy } from '../../../../../../../components/bizy';
import { BizyChatMessage } from './bizy-chat-message';
import { useBizyChat } from './use-bizy-chat';

interface BizyChatProps {
	meetingId: string;
}

function getStatusMessage(isLoading: boolean, messageCount: number): string | null {
	if (!isLoading) return null;
	if (messageCount === 0) return 'Looking through your meeting history...';
	return 'Thinking...';
}

// typing indicator animation
const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
`;

function TypingIndicator() {
	return (
		<Box alignSelf="flex-start" maxWidth="85%" bg="bg.subtle" px={4} py={3} borderRadius="lg" borderBottomLeftRadius="sm">
			<Flex gap={1} align="center" height="16px">
				{[0, 1, 2].map((i) => (
					<Box
						key={i}
						width="8px"
						height="8px"
						borderRadius="full"
						bg="text.softest"
						animation={`${bounce} 1.4s ease-in-out infinite`}
						sx={{ animationDelay: `${i * 0.2}s` }}
					/>
				))}
			</Flex>
		</Box>
	);
}

export function BizyChat({ meetingId }: BizyChatProps) {
	const { messages, isLoading, bizyEmotion, sendMessage } = useBizyChat(meetingId);

	const [inputValue, setInputValue] = useState('');
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const statusMessage = getStatusMessage(isLoading, messages.length);

	// scroll to bottom on new messages or when animating
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const handleSend = () => {
		if (inputValue.trim() && !isLoading) {
			sendMessage(inputValue.trim());
			setInputValue('');
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<Flex direction="column" height="100%" position="relative">
			{/* floating bizy avatar - positioned absolutely over the scroll area */}
			<Flex
				position="absolute"
				top={0}
				left={0}
				right={0}
				zIndex={10}
				direction="column"
				align="center"
				pt={2}
				pb={2}
				bg="linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 70%, rgba(255,255,255,0) 100%)"
				pointerEvents="none"
			>
				{/* status message - above bizy */}
				{statusMessage && (
					<Text fontSize="xs" color="text.default" fontStyle="italic" mb={1}>
						{statusMessage}
					</Text>
				)}
				<Flex direction="column" align="center" bg="white" borderRadius="full" p={3} boxShadow="md" pointerEvents="auto">
					<Bizy state={isLoading ? 'thinking' : bizyEmotion} size="lg" />
				</Flex>
			</Flex>

			{/* messages area - scrolls behind bizy */}
			<Box flex={1} overflowY="auto" px={4} pt="120px" pb={4}>
				<Flex direction="column" gap={3} minHeight="100%" justifyContent="flex-end">
					{messages.length === 0 && !isLoading && (
						<Text color="text.softest" textAlign="center" py={4}>
							Ask me anything about your meeting history!
						</Text>
					)}
					{messages.map((message, index) => {
						const isLastMessage = index === messages.length - 1;
						const isStreamingLastMessage = isLastMessage && isLoading && message.role === 'assistant';
						return (
							<BizyChatMessage
								key={index}
								message={message}
								isStreaming={isStreamingLastMessage}
								onContentUpdate={isStreamingLastMessage ? scrollToBottom : undefined}
							/>
						);
					})}
					{isLoading && messages[messages.length - 1]?.role !== 'assistant' && <TypingIndicator />}
					<div ref={messagesEndRef} />
				</Flex>
			</Box>

			{/* input area */}
			<Flex gap={2} p={4} pt={0}>
				<Input
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder="Ask Bizy a question..."
					disabled={isLoading}
					aria-label="Chat message input"
				/>
				<IconButton aria-label="Send message" icon={<PaperAirplaneOutline />} onClick={handleSend} isDisabled={!inputValue.trim() || isLoading} />
			</Flex>
		</Flex>
	);
}
